import React, { useCallback, useEffect, useState } from "react";
import { Tabs, Tab, Button } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";

import { ChatLoadingState } from "@/components/Chat/ChatLoadingState";
import { PageTitle } from "@/components/UI/PageTitle";
import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { useAdminStore } from "@/stores/AdminStore";

import { AdminStatsCards } from "./AdminStatsCards";
import { UsersTab } from "./UsersTab";
import { WorkflowsTab } from "./WorkflowsTab";
import { AuditLogTab } from "./AuditLogTab";
import { TemplatesTab } from "./TemplatesTab";
import { PermissionMatrixModal } from "./PermissionMatrixModal";

// Note: Access control is handled by ProtectedRoute (requiredPermission="settings")
// in App.tsx — no need for a duplicate permission check here.

export const AdminPage: React.FC = () => {
  const { t } = useTranslation();
  const { adminClient } = useWebuildAPIClient();

  const {
    users,
    workflows,
    workflowUserCounts,
    auditLogs,
    isLoading,
    error,
    loadAll,
    loadUsers,
    loadAuditLogs,
  } = useAdminStore();

  const [isMatrixOpen, setIsMatrixOpen] = useState(false);

  const refreshUsers = useCallback(async () => {
    if (!adminClient) return;
    await loadUsers(adminClient);
  }, [adminClient, loadUsers]);

  const refreshAuditLogs = useCallback(
    async (actionFilter?: string) => {
      if (!adminClient) return;
      await loadAuditLogs(adminClient, { limit: 50, actionFilter });
    },
    [adminClient, loadAuditLogs],
  );

  useEffect(() => {
    if (!adminClient) return;
    loadAll(adminClient);
  }, [adminClient, loadAll]);

  if (isLoading) {
    return <ChatLoadingState message={t("Loading permissions...")} />;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4 p-8">
        <Icon className="text-danger" height={48} icon="solar:danger-triangle-outline" width={48} />
        <h2 className="text-xl font-bold">{t("Something went wrong")}</h2>
        <p className="text-default-500">{t(error)}</p>
        <Button
          color="primary"
          startContent={<Icon icon="solar:refresh-outline" width={18} />}
          onPress={() => adminClient && loadAll(adminClient)}
        >
          {t("Retry")}
        </Button>
      </div>
    );
  }

  const adminCount = users.filter((u) => u.role === "Admin").length;
  const managerCount = users.filter((u) => u.role === "Manager").length;
  const userCount = users.filter((u) => u.role === "User").length;

  return (
    <div className="flex flex-col h-full w-full overflow-hidden">
      {/* Sticky header area */}
      <div className="shrink-0 p-4 pb-0 space-y-4">
        <div className="flex items-center justify-between">
          <PageTitle
            centered
            dataId="admin-title"
            icon="solar:shield-user-outline"
            subtitle="Manage user roles, workflow access, and view audit history"
            title="User Permissions"
          />
          <Button
            className="hidden md:flex"
            startContent={<Icon icon="solar:info-circle-outline" width={18} />}
            variant="flat"
            onPress={() => setIsMatrixOpen(true)}
          >
            {t("Permission Matrix")}
          </Button>
        </div>

        <AdminStatsCards
          adminCount={adminCount}
          managerCount={managerCount}
          totalUsers={users.length}
          totalWorkflows={workflows.length}
          userCount={userCount}
        />
      </div>

      {/* Tabs with scrollable content */}
      <div className="flex flex-col flex-1 min-h-0 px-4">
        <Tabs
          aria-label={t("Admin tabs")}
          classNames={{ panel: "flex-1 min-h-0 overflow-y-auto pb-4" }}
          color="primary"
          variant="underlined"
        >
          <Tab key="users" title={t("User Permissions")}>
            <UsersTab users={users} onUsersChanged={refreshUsers} />
          </Tab>
          <Tab key="workflows" title={t("Workflows")}>
            <WorkflowsTab
              users={users}
              workflowUserCounts={workflowUserCounts}
              workflows={workflows}
            />
          </Tab>
          <Tab key="audit" title={t("Audit Log")}>
            <AuditLogTab logs={auditLogs} onRefresh={refreshAuditLogs} />
          </Tab>
          <Tab key="templates" title={t("Templates")}>
            <TemplatesTab />
          </Tab>
        </Tabs>
      </div>

      <PermissionMatrixModal isOpen={isMatrixOpen} onClose={() => setIsMatrixOpen(false)} />
    </div>
  );
};
